import Manager from 'coreClasses/Manager';
import CountryModel from './CountryModel';
import {receiveCountries, selectCountries, receiveCountryCurrencyRates} from 'coreStore/data/configurations/index';
import type {Country} from 'coreStore/data/configurations/types';
import {getDefaultSite} from 'coreUtils/siteUtils';

interface IGetCountriesParams {
	DEFAULT_CONTINENTS?: string[];
	mergeAmericas?: boolean;
}

/**
 * Represents CountriesMgr
 */
class CountriesMgr extends Manager {
	private cache = {
		countries: [],
	};
	private initialized = false;

	/**
	 * Fetches all countries
	 */
	async fetchAllCountries(): Promise<void> {
		const {countries} =
			this.managerRepository.getStaticCacheMgr().getCountries()
			|| await this.managerRepository
				.getSfccAPI()
				.getAllCountries();

		const parsedCountries = countries.map((country) => ({
			countryCode: country.id,
			continents: country.continents || [],
			regionCodes: country.regionCodes || [],
			currencyCode: country.currency,
			hreflangLanguageCodes: country.hreflangLanguageCodes || [],
			currencyConfig: country.currencyConfig || {},
			currencySymbol: country.currencySymbol,
			isDDPOptionEnabled: country.isDDPOptionEnabled || false,
			isDDPBadgeEnabledOnPDP: country.isDDPBadgeEnabledOnPDP || false,
			isVirtualTryOnDisabled: country.isVirtualTryOnDisabled || false,
			isVirtualMirrorDisabled: country.isVirtualMirrorDisabled || false,
			isBeautyVirtualTryOnDisabled: country.isBeautyVirtualTryOnDisabled || false,
			taxRate: country.taxRate || 0,
			localeSettings: country.localeSettings || {},
			siteId: country.siteId || '',
			socialLoginButtons: country.socialLoginButtons || '',
			priceBooksGroup: country.priceBooksGroup || '',
			availableLanguages: country.availableLanguages || [],
			canonicalLinksMapping: country.canonicalLinksMapping || {},
			externalUrl: country.externalUrl || '',
			unitOfMeasure: country.unitOfMeasure || '',
			returnFee: Number(country.returnFee) > 0 ? Number(country.returnFee) : null,
			returnAuthCustomerGroupsToExclude: this.parseCustomerGroups(country.returnAuthCustomerGroupsToExclude),
			returnOrderAmountThreshold: Number(country.returnOrderAmountTreshold) > 0
				? Number(country.returnOrderAmountTreshold)
				: null
		}));

		// reset cache on new data fetching
		this.cache.countries = [];

		const countriesSorted = parsedCountries.sort((a, b) => a.label?.localeCompare(b?.label));

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCountries(countriesSorted));
		this.initialized = true;
	}

	/**
	 * Parses the comma separated customer group IDs coming from the country configuration
	 * @param {string|string[]} customerGroups - customer group IDs
	 * @returns {string[]} customer group IDs
	 */
	private parseCustomerGroups(customerGroups: string | string[]): string[] {
		const groups = Array.isArray(customerGroups)
			? customerGroups
			: (typeof customerGroups === 'string' ? customerGroups.split(',') : []);

		return groups.map((group) => String(group).trim()).filter(Boolean);
	}

	/**
	 * Returns countries
	 * @returns {Array<CountryModel>} countries
	 */
	getCountries(): CountryModel[] {
		if (this.cache.countries.length > 0) {
			return this.cache.countries;
		}

		this.cache.countries = selectCountries(this.managerRepository.getStoreMgr().getStore().getState())?.map(
			(country) => new CountryModel(country),
		) || [];

		return this.cache.countries;
	}

	/**
	 * Get countries filtered by continent.
	 *
	 * @param {IGetCountriesParams} [params] - The parameters for filtering countries.
	 * @param {string[]} [params.DEFAULT_CONTINENTS] - An array of continent codes to filter countries.
	 * @param {boolean} [params.mergeAmericas] - When true, merges SA countries into NA and removes SA key.
	 * @returns {{ [key: string]: CountryModel[] }} - An object where each key represents a continent code, and the corresponding value is an array of CountryModel instances in that continent.
	 */
	getCountriesByContinent(params: IGetCountriesParams = {}): { [key: string]: CountryModel[] } {
		const countriesByContinent: { [key: string]: CountryModel[] } = {};

		this.getCountries().forEach((country) => {
			country.getContinents().forEach((continent) => {
				if (params.DEFAULT_CONTINENTS.includes(continent)) {
					if (!countriesByContinent[continent]) {
						countriesByContinent[continent] = [];
					}

					countriesByContinent[continent].push(country);
				}
			});
		});

		if (params.mergeAmericas) {
			countriesByContinent.NA = [
				...(countriesByContinent.SA || []),
				...(countriesByContinent.NA || []),
			];
			delete countriesByContinent.SA;
		}

		return countriesByContinent;
	}

	/**
	 * Returns the country by country code
	 * @param {string} countryCode - country code
	 * @returns {CountryModel} country
	 */
	getCountryByCode(countryCode: string): CountryModel {
		return this.getCountries().find((country) => (country as any).country.countryCode === countryCode);
	}

	/**
	 * Returns the default country
	 * @returns {CountryModel} default country
	 */
	getDefaultCountry(): CountryModel {
		const {defaultCountry, defaultCurrency} = getDefaultSite();

		if (this.initialized) {
			return this.getCountryByCode(defaultCountry);
		} else {
			return new CountryModel({
				countryCode: defaultCountry,
				regionCodes: [],
				currencyCode: defaultCurrency,
			} as Country);
		}
	}

	/**
	 * Updates currency conversion rates for a country
	 * @param {String} countryCode country code
	 * @returns {Promise}
	 */
	async updateCurrencyConversionRates(countryCode: string): Promise<void> {
		const country = this.getCountryByCode(countryCode);
		const countryAdditionalCurrencies = country.getCurrenciesFromConfig();
		const from = country.getCurrencyCode();
		const currencyConversionRates = this.managerRepository.getConfigurationsMgr().get('currencyConversionRates') || {};

		if (countryAdditionalCurrencies.length > 0) {
			const ratesToFetch = [];

			countryAdditionalCurrencies.forEach((currency) => {
				if (!currencyConversionRates || !currencyConversionRates[from] || !currencyConversionRates[from][currency]) {
					ratesToFetch.push(currency);
				}
			});

			if (ratesToFetch.length === 0) {
				return Promise.resolve();
			}

			return this.managerRepository
				.getSfccAPI()
				.getCurrencyConversionRate(from, ratesToFetch)
				.then(({rates}) => {
					if (rates) {
						Object.keys(rates).forEach((key) => {
							if (rates && rates[key]) {
								this.managerRepository
									.getStoreMgr()
									.getStore()
									.dispatch(
										receiveCountryCurrencyRates({
											from: from,
											to: key,
											rate: rates[key].value,
										}),
									);
							}
						});

						this.managerRepository.getConfigurationsMgr().invalidateCache('currencyConversionRates');
					}

					return rates;
				});
		} else {
			return Promise.resolve();
		}
	}
}

export default CountriesMgr;
